import { initializeWebGPU } from '../webgpu/setup.js';
import { createTextures, destroyResources, createUniformBuffer } from '../webgpu/resources.js';
import { createLUTComputePipeline, createLUTBindGroup, computeWorkgroupDispatch } from '../webgpu/pipeline.js';
import { uploadAllData, readResultData } from '../webgpu/dataTransfer.js';
/**
 * 支持的LUT文件格式
 */
export const SUPPORTED_LUT_FORMATS = {
    'text/plain': 'cube',
    'application/octet-stream': 'cube',
    'text/cube': 'cube'
};
/**
 * 检测LUT文件格式
 * @param blob LUT文件Blob对象
 * @returns 格式信息
 */
function detectLutFormat(blob) {
    const format = blob.type.toLowerCase();
    const extension = SUPPORTED_LUT_FORMATS[format];
    return {
        format,
        supported: !!extension,
        extension: extension || 'unknown'
    };
}
/**
 * 解析CUBE格式的LUT文件（优化版本）
 * 优化点：
 * 1. 使用更高效的字符串解析
 * 2. 减少内存分配和数组操作
 * 3. 使用预分配数组
 * 4. 优化循环结构
 * @param content CUBE文件内容
 * @returns 解析后的LUT数据
 */
export function parseCubeFile(content) {
    // 优化：使用更高效的字符串分割，避免多次创建数组
    const lines = content.split('\n');
    const filteredLines = [];
    // 优化：预过滤有效行，减少后续处理
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i]?.trim();
        if (line && !line.startsWith('#')) {
            filteredLines.push(line);
        }
    }
    let size = 64; // 默认尺寸
    let title;
    let domain;
    // 优化：预计算LUT值数组大小，避免动态扩容
    const expectedValues = size * size * size * 3; // RGB值
    const lutValues = new Array(expectedValues);
    let lutIndex = 0;
    // 优化：使用更高效的头部信息解析
    for (let i = 0; i < filteredLines.length; i++) {
        const line = filteredLines[i];
        if (line.startsWith('LUT_3D_SIZE')) {
            const sizeStr = line.split(' ')[1];
            if (sizeStr) {
                const newSize = parseInt(sizeStr);
                if (newSize > 0) {
                    size = newSize;
                    // 重新计算期望值数量
                    const newExpectedValues = size * size * size * 3;
                    if (lutIndex === 0) {
                        // 如果还没有开始解析LUT值，重新分配数组
                        lutValues.length = newExpectedValues;
                    }
                }
            }
        }
        else if (line.startsWith('TITLE')) {
            title = line.split(' ').slice(1).join(' ').replace(/"/g, '');
        }
        else if (line.startsWith('DOMAIN_MIN')) {
            const values = line.split(' ').slice(1).map(Number);
            domain = domain || { min: [0, 0, 0], max: [1, 1, 1] };
            if (values[0] !== undefined && values[1] !== undefined && values[2] !== undefined) {
                domain.min = [values[0], values[1], values[2]];
            }
        }
        else if (line.startsWith('DOMAIN_MAX')) {
            const values = line.split(' ').slice(1).map(Number);
            domain = domain || { min: [0, 0, 0], max: [1, 1, 1] };
            if (values[0] !== undefined && values[1] !== undefined && values[2] !== undefined) {
                domain.max = [values[0], values[1], values[2]];
            }
        }
        else {
            // 优化：使用更高效的LUT值解析
            const values = line.split(/\s+/);
            let valueIndex = 0;
            // 优化：直接解析数值，避免多次数组操作
            for (let j = 0; j < values.length && valueIndex < 3; j++) {
                const value = parseFloat(values[j]);
                if (!isNaN(value)) {
                    lutValues[lutIndex + valueIndex] = value;
                    valueIndex++;
                }
            }
            if (valueIndex >= 3) {
                lutIndex += 3;
            }
        }
    }
    // 验证数据完整性
    const finalExpectedValues = size * size * size * 3;
    if (lutIndex !== finalExpectedValues) {
        throw new Error(`LUT数据不完整: 期望${finalExpectedValues}个值，实际${lutIndex}个值`);
    }
    // 优化：使用更高效的数据格式转换
    const data = new Uint8Array(size * size * size * 4);
    // 优化：使用更高效的循环结构，减少计算开销
    for (let i = 0; i < lutIndex; i += 3) {
        const dataIndex = Math.floor(i * 4 / 3);
        const r = lutValues[i];
        const g = lutValues[i + 1];
        const b = lutValues[i + 2];
        if (r !== undefined && g !== undefined && b !== undefined) {
            // 优化：使用位运算进行快速转换
            data[dataIndex] = Math.floor(r * 255); // R
            data[dataIndex + 1] = Math.floor(g * 255); // G
            data[dataIndex + 2] = Math.floor(b * 255); // B
            data[dataIndex + 3] = 255; // A
        }
    }
    const result = {
        data,
        size
    };
    if (title !== undefined) {
        result.title = title;
    }
    if (domain !== undefined) {
        result.domain = domain;
    }
    return result;
}
/**
 * 处理LUT文件
 * @param lutPath LUT文件路径
 * @returns LUT文件处理结果
 */
export async function processLutFile(lutPath) {
    try {
        const response = await fetch(lutPath);
        if (!response.ok) {
            throw new Error(`无法加载LUT文件: ${response.statusText}`);
        }
        const blob = await response.blob();
        // 检测LUT格式
        const formatInfo = detectLutFormat(blob);
        if (!formatInfo.supported) {
            throw new Error(`不支持的LUT格式: ${formatInfo.format}`);
        }
        const text = await blob.text();
        const data = parseCubeFile(text);
        return {
            data: data.data,
            blob,
            blobURL: URL.createObjectURL(blob),
            fileName: lutPath.split('/').pop() || `lut.${formatInfo.extension}`,
            fileSize: blob.size
        };
    }
    catch (error) {
        throw new Error(`处理LUT文件失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
}
/**
 * 处理LUT数据（通用版本，支持多种数据源）
 * @param source LUT数据源
 * @param fileName 文件名（可选）
 * @returns LUT文件处理结果
 */
export async function processLutData(source, fileName) {
    try {
        let text;
        let blob;
        let formatInfo;
        if (typeof source === 'string') {
            // 从URL加载
            const response = await fetch(source);
            if (!response.ok) {
                throw new Error(`无法加载LUT文件: ${response.statusText}`);
            }
            blob = await response.blob();
            text = await blob.text();
        }
        else if (source instanceof Blob) {
            // 直接从Blob处理
            blob = source;
            text = await blob.text();
        }
        else if (source instanceof ArrayBuffer) {
            // 从ArrayBuffer创建Blob
            blob = new Blob([source], { type: 'text/plain' });
            text = new TextDecoder().decode(source);
        }
        else {
            throw new Error('不支持的LUT数据源类型');
        }
        formatInfo = detectLutFormat(blob);
        if (!formatInfo.supported) {
            throw new Error(`不支持的LUT格式: ${formatInfo.format}`);
        }
        const data = parseCubeFile(text);
        return {
            data: data.data,
            blob,
            blobURL: URL.createObjectURL(blob),
            fileName: fileName || `lut.${formatInfo.extension}`,
            fileSize: blob.size
        };
    }
    catch (error) {
        throw new Error(`处理LUT数据失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
}
/**
 * 批量处理LUT文件
 * @param lutPaths LUT文件路径数组
 * @returns LUT文件处理结果数组
 */
export async function processLutFiles(lutPaths) {
    const results = [];
    // 并行处理所有LUT文件
    const promises = lutPaths.map(async (path, index) => {
        try {
            const result = await processLutFile(path);
            return { index, result, error: null };
        }
        catch (error) {
            return {
                index,
                result: null,
                error: error instanceof Error ? error.message : '未知错误'
            };
        }
    });
    const processedResults = await Promise.all(promises);
    // 按原始顺序整理结果
    for (const { index, result, error } of processedResults) {
        if (error) {
            throw new Error(`处理LUT文件 ${lutPaths[index]} 失败: ${error}`);
        }
        results[index] = result;
    }
    return results;
}
/**
 * 验证LUT数据有效性
 * @param data LUT数据
 * @param size LUT尺寸
 * @returns 是否有效
 */
export function validateLutData(data, size) {
    const expectedLength = size * size * size * 4; // RGBA格式
    return data.length === expectedLength;
}
/**
 * 获取LUT数据尺寸
 * @param data LUT数据
 * @returns LUT尺寸
 */
export function getLutSize(data) {
    const totalPixels = data.length / 4; // RGBA格式
    return Math.cbrt(totalPixels);
}
/**
 * 使用LUT处理图像
 * @param image 输入图像对象
 * @param lutData LUT数据
 * @param options 处理选项
 * @returns 处理结果
 */
export async function processImageWithLUT(image, lutData, options = {}) {
    const { intensity = 1.0, debug = false, highPerformance = false, maskData, maskIntensity = 1.0, enableMask = false, maskOffsetX = 0, maskOffsetY = 0, maskWidth, maskHeight } = options;
    let resources = null;
    let uniformBuffer = null;
    try {
        if (!image || !lutData) {
            throw new Error('输入图像或LUT数据无效');
        }
        // 计算LUT尺寸
        const lutSize = Math.cbrt(lutData.length / 4);
        if (!Number.isInteger(lutSize) || lutSize <= 0) {
            throw new Error(`无效的LUT数据大小: ${lutData.length}字节`);
        }
        if (debug) {
            console.log('开始LUT处理:', {
                imageSize: `${image.width}x${image.height}`,
                lutDataSize: lutData.length,
                lutSize: lutSize,
                intensity,
                highPerformance,
                enableMask,
                maskIntensity,
                maskData: maskData ? `${maskData.width}x${maskData.height}` : '无'
            });
        }
        const { device } = await initializeWebGPU();
        // 创建纹理资源，包括遮罩纹理
        const textures = createTextures(device, image, lutSize, enableMask && maskData ? maskData : undefined);
        resources = textures;
        // 创建统一缓冲区（包含intensity、lutSize、maskIntensity、enableMask和遮罩坐标参数）
        const uniformData = new Float32Array([
            intensity,
            lutSize,
            maskIntensity,
            enableMask ? 1.0 : 0.0,
            maskOffsetX || 0.0,
            maskOffsetY || 0.0,
            maskWidth || 0.0,
            maskHeight || 0.0
        ]);
        uniformBuffer = createUniformBuffer(device, uniformData);
        // 创建计算管线和绑定组
        const pipeline = createLUTComputePipeline(device, highPerformance);
        const bindGroup = createLUTBindGroup(device, pipeline, textures, uniformBuffer);
        // 上传数据，包括遮罩数据
        uploadAllData(device, textures, image, lutData, lutSize, enableMask && maskData ? maskData : undefined);
        // 计算最优的工作组调度参数
        const [workgroupsX, workgroupsY] = computeWorkgroupDispatch(image.width, image.height, highPerformance);
        if (debug) {
            console.log('工作组调度参数:', {
                workgroupsX,
                workgroupsY,
                workgroupSize: highPerformance ? '32x32' : '16x16',
                totalWorkgroups: workgroupsX * workgroupsY
            });
        }
        // 执行计算
        const commandEncoder = device.createCommandEncoder();
        const computePass = commandEncoder.beginComputePass();
        computePass.setPipeline(pipeline);
        computePass.setBindGroup(0, bindGroup);
        computePass.dispatchWorkgroups(workgroupsX, workgroupsY);
        computePass.end();
        device.queue.submit([commandEncoder.finish()]);
        // 读取结果
        const resultData = await readResultData(device, textures.outputTexture, image.width, image.height);
        if (debug) {
            console.log('LUT处理完成');
        }
        return { success: true, result: resultData };
    }
    catch (error) {
        const errorMessage = error instanceof Error ? error.message : '未知错误';
        console.error('LUT处理失败:', errorMessage);
        return { success: false, error: errorMessage };
    }
    finally {
        // 清理资源
        if (resources) {
            destroyResources(resources);
        }
        if (uniformBuffer) {
            uniformBuffer.destroy();
        }
    }
}
/**
 * 验证LUT数据格式
 * @param lutData LUT数据
 * @param expectedSize 期望的LUT尺寸
 * @returns 验证结果
 */
export function validateLUTData(lutData, expectedSize = 64) {
    const expectedBytes = expectedSize * expectedSize * expectedSize * 4; // RGBA
    return lutData.length === expectedBytes;
}
/**
 * 创建默认LUT数据（单位矩阵，不改变颜色）
 * @param size LUT尺寸
 * @returns 默认LUT数据
 */
export function createDefaultLUT(size = 64) {
    const data = new Uint8Array(size * size * size * 4);
    for (let b = 0; b < size; b++) {
        for (let g = 0; g < size; g++) {
            for (let r = 0; r < size; r++) {
                const index = (b * size * size + g * size + r) * 4;
                data[index] = Math.floor((r / (size - 1)) * 255); // R
                data[index + 1] = Math.floor((g / (size - 1)) * 255); // G
                data[index + 2] = Math.floor((b / (size - 1)) * 255); // B
                data[index + 3] = 255; // A
            }
        }
    }
    return data;
}
//# sourceMappingURL=lutProcessor.js.map